import axios from 'axios';

// Automatically detect backend URL
// In production/deployed: use relative URL (empty string)
// In local dev: use localhost:8001
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

console.log('Backend URL:', BACKEND_URL || 'relative (same domain)');
console.log('API Base URL:', API);

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY_BASE = 1000; // 1 second base delay

// Helper function for exponential backoff delay
const getRetryDelay = (retryCount) => {
  return Math.min(RETRY_DELAY_BASE * Math.pow(2, retryCount), 10000); // Max 10 seconds
};

// Create axios instance with timeout and retry config
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('tekstar_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add retry count to config
    config._retryCount = config._retryCount || 0;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors with retry logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const retryCount = originalRequest._retryCount || 0;
    
    // Retry on network errors, 520 (Cloudflare), 502, 503, 504 (gateway errors)
    const isRetryableError = 
      !error.response || // Network error (no response)
      status === 520 ||  // Cloudflare unknown error
      status === 502 ||  // Bad gateway
      status === 503 ||  // Service unavailable
      status === 504 ||  // Gateway timeout
      error.code === 'ECONNABORTED'; // Timeout
    
    if (isRetryableError && retryCount < MAX_RETRIES) {
      originalRequest._retryCount = retryCount + 1;
      const delay = getRetryDelay(retryCount);
      
      console.log(`Retrying request (attempt ${retryCount + 1}/${MAX_RETRIES}) after ${delay}ms...`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return api(originalRequest);
    }
    
    // Handle 401 (unauthorized)
    if (status === 401) {
      localStorage.removeItem('tekstar_token');
      localStorage.removeItem('tekstar_user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    // Enhance error message for user-friendly display
    if (!error.response) {
      error.userMessage = 'Network connection issue. Please check your internet and try again.';
    } else if (status === 520 || status === 502 || status === 503 || status === 504) {
      error.userMessage = 'Server is temporarily busy. Please try again in a moment.';
    } else if (error.code === 'ECONNABORTED') {
      error.userMessage = 'Request timed out. Please check your connection and try again.';
    }
    
    return Promise.reject(error);
  }
);

export default api;